import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'lokos-select',
    templateUrl: './select.component.html',
    styleUrl: './select.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class SelectComponent implements OnChanges, OnInit {
    @Input({ required: false }) control: FormControl = new FormControl();
    @Input({ required: false }) countField = '';
    @Input({ required: false }) codeField = '';
    @Input({ required: false }) disabled = false;
    @Input({ required: false }) displayName = '';
    @Input({ required: false }) getName = '';
    @Input({ required: false }) label = '';
    @Input({ required: false }) multiSelect = false;
    @Input({ required: false }) value = '';
    @Input({ required: false }) sortOptions = true;
    @Input({ required: true }) optionsList: any[] = [];
    @Input({ required: true }) placeholder = 'placeholder';
    @Input({ required: true }) required = false;
    @Output() afterSelectionChange = new EventEmitter();

    allSelected = false;
    indeterminate = false;
    private internalValue: any[] = [];
    private sortedCache: any[] = [];
    private cacheSrc: any[] | null = null;
    private cacheLen = -1;
    private cacheKey = '';

    constructor(
        private translate: TranslateService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.control.statusChanges.subscribe(() => {
            this.cdr.markForCheck();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['disabled']) {
            if (this.disabled) {
                this.control.disable({ emitEvent: false });
            } else {
                this.control.enable({ emitEvent: false });
            }
        }
        if (changes['value']) {
            this.setInternalValue(this.value);
            this.control.setValue(this.value, { emitEvent: false });
            this.updateSelectAllState();
        }
        if (changes['optionsList'] || changes['getName']) {
            // Re-apply value when options arrive (value may have been set before options were available)
            if (this.value && this.optionsList?.length) {
                this.control.setValue(this.value, { emitEvent: false });
            }
            this.updateSelectAllState();
        }
    }

    // Sorted view of optionsList. Computed lazily and cached so it survives
    // in-place mutations of optionsList (push) which don't trigger ngOnChanges.
    get displayOptionsList(): any[] {
        const list = this.optionsList ?? [];
        if (!this.sortOptions) {
            return list;
        }
        if (
            list === this.cacheSrc &&
            list.length === this.cacheLen &&
            this.cacheKey === this.displayName
        ) {
            return this.sortedCache;
        }
        this.cacheSrc = list;
        this.cacheLen = list.length;
        this.cacheKey = this.displayName;
        this.sortedCache = [...list].sort((a, b) =>
            this.getOptionLabel(a).localeCompare(this.getOptionLabel(b), undefined, {
                sensitivity: 'base',
                numeric: true,
            })
        );
        return this.sortedCache;
    }

    private getOptionLabel(option: any): string {
        const raw = this.displayName ? option?.[this.displayName] : option;
        if (raw === null || raw === undefined) return '';
        return this.translate.instant(String(raw));
    }

    // Display text shown per option: translated name, optionally suffixed
    // with " (code)" when codeField is set and the option carries that code.
    getOptionDisplay(option: any): string {
        const label = this.getOptionLabel(option);
        const code = this.codeField ? option?.[this.codeField] : '';
        return code ? `${label} (${code})` : label;
    }

    onSelectionChange(event: MatSelectChange) {
        if (!this.multiSelect) {
            this.afterSelectionChange.emit(event);
            return;
        }

        let selectedValues = [...event.value];
        const allOptionValues = this.getAllOptionValues();

        const selectAllClicked = selectedValues.includes('selectAll');

        if (selectAllClicked) {
            if (this.allSelected) {
                selectedValues = [];
            } else {
                selectedValues = [...allOptionValues];
            }
        } else {
            selectedValues = selectedValues.filter(
                (val) => val !== 'selectAll'
            );
        }

        this.setInternalValue(selectedValues);

        this.control.setValue(selectedValues, { emitEvent: false });

        this.updateSelectAllState(selectedValues);

        this.afterSelectionChange.emit({
            source: event.source,
            value: selectedValues,
            displayValue: selectedValues.join(', '),
        });
    }

    private setInternalValue(value: any) {
        if (Array.isArray(value)) {
            this.internalValue = value;
        } else if (typeof value === 'string' && value.trim() !== '') {
            this.internalValue = value.split(',').map((v) => v.trim());
        } else {
            this.internalValue = [];
        }
    }

    private getAllOptionValues(): any[] {
        return this.optionsList.map((option) =>
            this.getName ? option[this.getName] : option
        );
    }

    private updateSelectAllState(selectedValues: any[] = []) {
        if (!this.multiSelect) return;

        const allOptionValues = this.getAllOptionValues();
        const selectedCount = allOptionValues.filter((val) =>
            selectedValues.includes(val)
        ).length;

        this.allSelected =
            selectedCount === allOptionValues.length &&
            allOptionValues.length > 0;
        this.indeterminate =
            selectedCount > 0 && selectedCount < allOptionValues.length;
    }

    getErrorMessage() {
        const label = this.label
            ? this.translate.instant(this.label)
            : this.translate.instant('thisField');

        if (this.control.hasError('required')) {
            return `${label} ${this.translate.instant('isRequired')}`;
        }
        return '';
    }

    getSelectedDisplay(): string {
        const value = this.control?.value;
        if (value === null || value === undefined || value === '') return '';
        const selected = this.optionsList?.find((o: any) =>
            this.getName ? o[this.getName] === value : o === value
        );
        if (!selected) return '';
        return this.getOptionDisplay(selected);
    }
}
